import React, { forwardRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { AlertCircle, Eye, EyeOff, Search } from 'lucide-react';

export interface InputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    error?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    label?: string;
    helperText?: string;
    containerClassName?: string;
    variant?: 'default' | 'filled' | 'ghost' | 'error';
    inputSize?: 'sm' | 'default' | 'lg';
    showPasswordToggle?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            type,
            error,
            leftIcon,
            rightIcon,
            label,
            helperText,
            containerClassName,
            variant = 'default',
            inputSize = 'default',
            showPasswordToggle = true,
            disabled,
            ...props
        },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        const [isFocused, setIsFocused] = useState(false);

        const isPassword = type === 'password';
        const finalType = isPassword && showPassword ? 'text' : type;
        const hasError = !!error;

        // Variant styles - using ThreatScope color system
        const variantStyles = {
            default: cn(
                "bg-background-light-secondary dark:bg-background-dark-secondary",
                "border border-border-light dark:border-border-dark-subtle",
                "focus:bg-background-light dark:focus:bg-background-dark-elevated",
                "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                "hover:border-border-light dark:hover:border-border-dark",
                !hasError && isFocused && "border-primary-500 ring-2 ring-primary-500"
            ),
            filled: cn(
                "bg-background-light-elevated dark:bg-background-dark-elevated",
                "border border-transparent",
                "focus:bg-background-light dark:focus:bg-background-dark-secondary",
                "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                "hover:bg-background-light-secondary dark:hover:bg-background-dark-secondary"
            ),
            ghost: cn(
                "bg-transparent",
                "border border-border-light dark:border-border-dark-subtle",
                "focus:bg-background-light-secondary dark:focus:bg-background-dark-secondary",
                "focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
                "hover:bg-background-light-secondary dark:hover:bg-background-dark-secondary"
            ),
            error: cn(
                "bg-background-light-secondary dark:bg-background-dark-secondary",
                "border border-red-300 dark:border-red-700",
                "focus:bg-background-light dark:focus:bg-background-dark-elevated",
                "focus:ring-2 focus:ring-red-500 focus:border-red-500"
            )
        };

        // Size styles
        const sizeStyles = {
            sm: "h-8 px-3 py-1 text-xs",
            default: "h-10 px-3 py-2 text-sm",
            lg: "h-12 px-4 py-3 text-base"
        };

        const iconSize = {
            sm: "h-3 w-3",
            default: "h-4 w-4",
            lg: "h-5 w-5"
        };

        // Icon container styles for better centering
        const iconContainerStyles = {
            sm: "h-8 w-8",
            default: "h-10 w-10",
            lg: "h-12 w-12"
        };

        // Auto-add search icon for search type
        const finalLeftIcon = type === 'search' && !leftIcon ? <Search className={cn(iconSize[inputSize], "text-text-muted-light dark:text-text-muted-dark")} /> : leftIcon;

        // Password toggle icon
        const passwordToggleIcon = isPassword && showPasswordToggle ? (
            <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={cn(
                    "flex items-center justify-center text-text-muted-light dark:text-text-muted-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors duration-200",
                    iconContainerStyles[inputSize]
                )}
                disabled={disabled}
                tabIndex={-1}
            >
                {showPassword ? <EyeOff className={iconSize[inputSize]} /> : <Eye className={iconSize[inputSize]} />}
            </button>
        ) : null;

        const finalRightIcon = passwordToggleIcon || rightIcon;

        return (
            <div className={cn("w-full space-y-1", containerClassName)}>
                {label && (
                    <label className="block text-sm font-medium font-grotesk text-text-primary-light dark:text-text-primary-dark">
                        {label}
                    </label>
                )}

                <div className="relative">
                    {finalLeftIcon && (
                        <div className={cn(
                            "absolute left-0 inset-y-0 flex items-center justify-center z-10",
                            iconContainerStyles[inputSize]
                        )}>
                            {finalLeftIcon}
                        </div>
                    )}

                    <input
                        type={finalType}
                        className={cn(
                            "flex w-full rounded-lg font-roboto text-text-primary-light dark:text-text-primary-dark transition-all duration-200",
                            "placeholder:text-text-muted-light dark:placeholder:text-text-muted-dark",
                            "disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none",
                            "[&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute",
                            "[&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full",
                            "[&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer",
                            sizeStyles[inputSize],
                            variantStyles[hasError ? 'error' : variant],
                            finalLeftIcon && "pl-10",
                            finalRightIcon && "pr-10",
                            className
                        )}
                        ref={ref}
                        disabled={disabled}
                        onFocus={(e) => {
                            setIsFocused(true);
                            props.onFocus?.(e);
                        }}
                        onBlur={(e) => {
                            setIsFocused(false);
                            props.onBlur?.(e);
                        }}
                        {...props}
                    />

                    {finalRightIcon && (
                        <div className={cn(
                            "absolute right-0 inset-y-0 flex items-center justify-center",
                            iconContainerStyles[inputSize]
                        )}>
                            {finalRightIcon}
                        </div>
                    )}
                </div>

                {(error || helperText) && (
                    <div className="flex items-start space-x-1">
                        {error && (
                            <>
                                <AlertCircle className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-red-500 font-roboto">{error}</p>
                            </>
                        )}
                        {!error && helperText && (
                            <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-roboto">{helperText}</p>
                        )}
                    </div>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';